import React, { useEffect, useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon, GlobeAltIcon, ChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { bandwidthService, BandwidthSummary, ServiceBandwidth } from '../../services/bandwidthService';
import { awsService } from '../../services/awsService';
import LoadingSpinner from '../LoadingSpinner';
import Toast, { ToastType } from '../Toast';

interface BandwidthDashboardProps {
  onDataLoad?: (totalBandwidthGB: number, totalCost: number) => void;
}

const BandwidthDashboard: React.FC<BandwidthDashboardProps> = ({ onDataLoad }) => {
  const [bandwidthData, setBandwidthData] = useState<BandwidthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    fetchBandwidthData();
  }, []);

  const fetchBandwidthData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bandwidthService.getBandwidthSummary();
      setBandwidthData(data);
      
      // Calculate total bandwidth (egress + ingress) and notify parent
      if (onDataLoad && data) {
        const totalGB = data.currentMonth.totals.totalEgressGB + data.currentMonth.totals.totalIngressGB;
        const totalCost = data.currentMonth.totals.estimatedCost;
        onDataLoad(totalGB, totalCost);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch bandwidth data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncBandwidth = async () => {
    try {
      setIsSyncing(true);
      setError(null);
      
      // Get the credential to sync with
      const response = await awsService.getCredentials();
      if (!response || !response.credentials || response.credentials.length === 0) {
        setToast({ message: 'No AWS credentials configured', type: 'error' });
        setIsSyncing(false);
        return;
      }
      
      // Use the first active credential
      const activeCredential = response.credentials.find((c: any) => c.isActive) || response.credentials[0];
      
      // Get regions to sync
      const regions = [
        'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
        'eu-west-1', 'eu-west-2', 'eu-central-1',
        'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'
      ];
      
      // Show info toast and start background sync
      setToast({ message: 'Bandwidth sync started. This may take a few minutes...', type: 'info' });
      setIsSyncing(false); // Allow UI to be responsive
      
      // Perform sync in background
      bandwidthService.syncBandwidthData(activeCredential.id, regions)
        .then(async () => {
          // Success - refresh data and show success toast
          await fetchBandwidthData();
          setToast({ message: 'Bandwidth data synced successfully!', type: 'success' });
        })
        .catch((err: any) => {
          // Error - show error toast
          const errorMessage = err.response?.data?.error || 'Failed to sync bandwidth data';
          setToast({ message: errorMessage, type: 'error' });
        });
      
    } catch (err: any) {
      setIsSyncing(false);
      const errorMessage = err.response?.data?.error || 'Failed to start bandwidth sync';
      setToast({ message: errorMessage, type: 'error' });
    }
  };

  const formatGB = (gb: number): string => {
    if (gb < 1) return `${(gb * 1024).toFixed(2)} MB`;
    if (gb < 1024) return `${gb.toFixed(2)} GB`;
    return `${(gb / 1024).toFixed(2)} TB`;
  };

  const formatCost = (cost: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cost);
  };

  const getServiceIcon = (serviceName: string) => {
    const icons: Record<string, string> = {
      'EC2': '🖥️',
      'S3': '🗄️',
      'CloudFront': '🌐',
      'LoadBalancer': '⚖️',
      'NAT Gateway': '🔌',
      'EKS': '☸️'
    };
    return icons[serviceName] || '📊';
  };

  const getChangeIndicator = (change: number) => {
    if (change > 0) {
      return (
        <span className="flex items-center text-red-600 dark:text-red-400 text-sm">
          <ArrowUpIcon className="h-3 w-3 mr-1" />
          +{Math.abs(change).toFixed(2)}%
        </span>
      );
    } else if (change < 0) {
      return (
        <span className="flex items-center text-green-600 dark:text-green-400 text-sm">
          <ArrowDownIcon className="h-3 w-3 mr-1" />
          -{Math.abs(change).toFixed(2)}%
        </span>
      );
    }
    return <span className="text-gray-500 text-sm">No change</span>;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600 dark:text-red-400 p-4">{error}</div>;
  if (!bandwidthData) return <div className="text-gray-500 p-4">No bandwidth data available</div>;

  const { currentMonth, previousMonth, trends } = bandwidthData;

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <GlobeAltIcon className="h-5 w-5 mr-2" />
            Bandwidth Usage & Costs
          </h3>
          {/* Recalculate button removed - use Full Sync in header instead */}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Egress Card */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Egress</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatGB(currentMonth.totals.totalEgressGB)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {currentMonth.month}
                </p>
              </div>
              <div className="text-right">
                <ArrowUpIcon className="h-8 w-8 text-orange-500 mb-2" />
                {getChangeIndicator(
                  previousMonth.totals.totalEgressGB > 0 
                    ? ((currentMonth.totals.totalEgressGB - previousMonth.totals.totalEgressGB) / previousMonth.totals.totalEgressGB) * 100
                    : 0
                )}
              </div>
            </div>
          </div>

          {/* Ingress Card */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Ingress</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatGB(currentMonth.totals.totalIngressGB)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {currentMonth.month}
                </p>
              </div>
              <div className="text-right">
                <ArrowDownIcon className="h-8 w-8 text-blue-500 mb-2" />
                {getChangeIndicator(
                  previousMonth.totals.totalIngressGB > 0 
                    ? ((currentMonth.totals.totalIngressGB - previousMonth.totals.totalIngressGB) / previousMonth.totals.totalIngressGB) * 100
                    : 0
                )}
              </div>
            </div>
          </div>

          {/* Cost Card */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Estimated Cost</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatCost(currentMonth.totals.estimatedCost)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {currentMonth.month}
                </p>
              </div>
              <div className="text-right">
                <ChartBarIcon className="h-8 w-8 text-green-500 mb-2" />
                {getChangeIndicator(
                  previousMonth.totals.estimatedCost > 0 
                    ? ((currentMonth.totals.estimatedCost - previousMonth.totals.estimatedCost) / previousMonth.totals.estimatedCost) * 100
                    : 0
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Month Comparison */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
            Month-over-Month Comparison
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Egress Comparison */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-5 border border-orange-200 dark:border-orange-800">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-sm font-medium text-orange-900 dark:text-orange-200">
                  Total Egress
                </h5>
                <ArrowUpIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mb-1">{previousMonth.month}</p>
                  <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                    {formatGB(previousMonth.totals.totalEgressGB)}
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-orange-300 dark:border-orange-700"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-2 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 text-sm text-orange-600 dark:text-orange-400">
                      {previousMonth.totals.totalEgressGB > 0 
                        ? ((currentMonth.totals.totalEgressGB - previousMonth.totals.totalEgressGB) / previousMonth.totals.totalEgressGB * 100).toFixed(1)
                        : '0'
                      }% change
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mb-1">{currentMonth.month}</p>
                  <p className="text-xl font-bold text-orange-900 dark:text-orange-100">
                    {formatGB(currentMonth.totals.totalEgressGB)}
                  </p>
                </div>
              </div>
            </div>

            {/* Ingress Comparison */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-5 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  Total Ingress
                </h5>
                <ArrowDownIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-1">{previousMonth.month}</p>
                  <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                    {formatGB(previousMonth.totals.totalIngressGB)}
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-blue-300 dark:border-blue-700"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-2 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 text-sm text-blue-600 dark:text-blue-400">
                      {previousMonth.totals.totalIngressGB > 0 
                        ? ((currentMonth.totals.totalIngressGB - previousMonth.totals.totalIngressGB) / previousMonth.totals.totalIngressGB * 100).toFixed(1)
                        : '0'
                      }% change
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-1">{currentMonth.month}</p>
                  <p className="text-xl font-bold text-blue-900 dark:text-blue-100">
                    {formatGB(currentMonth.totals.totalIngressGB)}
                  </p>
                </div>
              </div>
            </div>

            {/* Cost Comparison */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-5 border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-sm font-medium text-green-900 dark:text-green-200">
                  Total Cost
                </h5>
                <ChartBarIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-green-700 dark:text-green-300 mb-1">{previousMonth.month}</p>
                  <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                    {formatCost(previousMonth.totals.estimatedCost)}
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-green-300 dark:border-green-700"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-2 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 text-sm text-green-600 dark:text-green-400">
                      {previousMonth.totals.estimatedCost > 0 
                        ? ((currentMonth.totals.estimatedCost - previousMonth.totals.estimatedCost) / previousMonth.totals.estimatedCost * 100).toFixed(1)
                        : '0'
                      }% change
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-green-700 dark:text-green-300 mb-1">{currentMonth.month}</p>
                  <p className="text-xl font-bold text-green-900 dark:text-green-100">
                    {formatCost(currentMonth.totals.estimatedCost)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Breakdown */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Bandwidth by Service
          </h4>
          <div className="space-y-2">
            {currentMonth.byService.map((service: ServiceBandwidth) => {
              const prevService = previousMonth.byService.find(s => s.serviceName === service.serviceName);
              const costChange = prevService 
                ? ((service.estimatedCost - prevService.estimatedCost) / prevService.estimatedCost) * 100
                : 0;

              return (
                <div
                  key={service.serviceName}
                  className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedService === service.serviceName ? 'ring-2 ring-indigo-500' : ''
                  }`}
                  onClick={() => setSelectedService(
                    selectedService === service.serviceName ? null : service.serviceName
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getServiceIcon(service.serviceName)}</span>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {service.serviceName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {service.resourceCount} resource{service.resourceCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatCost(service.estimatedCost)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ↑ {formatGB(service.totalEgressGB)} | ↓ {formatGB(service.totalIngressGB)}
                      </p>
                    </div>
                  </div>
                  {selectedService === service.serviceName && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Current Month</p>
                          <p className="font-medium">↑ {formatGB(service.totalEgressGB)} | ↓ {formatGB(service.totalIngressGB)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Previous Month</p>
                          <p className="font-medium">
                            ↑ {formatGB(prevService?.totalEgressGB || 0)} | ↓ {formatGB(prevService?.totalIngressGB || 0)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-gray-500 dark:text-gray-400 text-xs">Cost Change</p>
                        {getChangeIndicator(costChange)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* No Data Message */}
        {currentMonth.byService.length === 0 && (
          <div className="text-center py-8">
            <GlobeAltIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              No bandwidth data available. Sync your AWS resources to see bandwidth metrics.
            </p>
          </div>
        )}
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default BandwidthDashboard;